// ============================================================
// usePlatformProjects — admin-flavored projection over the
// portfolio for Platform Control.
//
// Reuses useProjects() so we don't duplicate the live/demo split,
// then enriches the rows with platform-control-only fields:
//
//   - tier         — derived from health (unknown until backend lands)
//   - region       — placeholder; will land when sq_project_infra exists
//   - last_deploy  — null until the deploy runner exists
//
// The shape is intentionally distinct from the user-facing Project
// type so views in PlatformControl can evolve without rippling into
// the project-first journey.
// ============================================================

import { useMemo } from 'react'
import { useProjects } from './useProjects'
import type { Project } from './types'

export interface PlatformProject {
  id: string
  name: string
  status?: string
  health_score?: number
  tier: 'standard' | 'priority' | 'unknown'
  region: string | null
  last_deploy_at: string | null
  updated_at?: string
}

export interface UsePlatformProjectsResult {
  projects: PlatformProject[]
  loading: boolean
  error: string | null
}

function project_to_platform(p: Project): PlatformProject {
  const health = p.health_score ?? 0
  const tier: PlatformProject['tier'] =
    health >= 90 ? 'priority' : health > 0 ? 'standard' : 'unknown'
  return {
    id: p.id,
    name: p.name,
    status: p.status,
    health_score: p.health_score,
    tier,
    region: null,
    last_deploy_at: null,
    updated_at: p.updated_at,
  }
}

export function usePlatformProjects(): UsePlatformProjectsResult {
  const { projects, loading, error } = useProjects()
  const platform = useMemo(() => projects.map(project_to_platform), [projects])
  return { projects: platform, loading, error }
}
